import { useCallback, useMemo, useState } from "react"
import type { GrupoCatalogo, ProdutoCatalogo } from "@/api"
import CardKiosk from "./card-kiosk"
import type { ItemPersonalizado } from "./dialog-passos"
import type { EscolhaDoDetalhe } from "./dialog-detalhe-produto"
import { temPassos } from "./passos"
import { useCarrinhoKiosk } from "./use-carrinho-kiosk"

interface OpcoesCardapioKiosk {
    seletorCompacto?: boolean
    /** Tocar na foto também abre os detalhes do produto (tablet); o ⓘ abre sempre. */
    detalhesNaFoto?: boolean
}

// A sugestão "Quer acompanhar?" está desligada por enquanto; as peças seguem em dialog-sugestao.tsx e lib/sugestoes.ts.
export function useCardapioKiosk(grupos: GrupoCatalogo[], { seletorCompacto = false, detalhesNaFoto = false }: OpcoesCardapioKiosk = {}) {
    const carrinho = useCarrinhoKiosk()
    const { adicionarSimples, removerSimples, aplicarPersonalizado, adicionarComDetalhes } = carrinho

    const [produtoPersonalizar, setProdutoPersonalizar] = useState<ProdutoCatalogo | null>(null)
    // Quantidade escolhida no detalhe, levada à escolha em passos; pelo botão do cartão é sempre 1.
    const [quantidadePersonalizar, setQuantidadePersonalizar] = useState(1)
    const [produtoDetalhe, setProdutoDetalhe] = useState<ProdutoCatalogo | null>(null)

    const categoriaPorProduto = useMemo(() => {
        const mapa = new Map<number, string>()
        for (const grupo of grupos) {
            for (const produto of grupo.produtos) {
                if (!mapa.has(produto.id)) mapa.set(produto.id, grupo.descricao)
            }
        }
        return mapa
    }, [grupos])

    const adicionar = useCallback((produto: ProdutoCatalogo) => {
        if (temPassos(produto)) {
            setQuantidadePersonalizar(1)
            setProdutoPersonalizar(produto)
            return
        }

        adicionarSimples(produto, categoriaPorProduto.get(produto.id))
    }, [adicionarSimples, categoriaPorProduto])

    const confirmarPersonalizado = useCallback((item: ItemPersonalizado) => {
        if (!produtoPersonalizar) return

        aplicarPersonalizado(produtoPersonalizar, item, categoriaPorProduto.get(produtoPersonalizar.id), quantidadePersonalizar)
        setProdutoPersonalizar(null)
        setQuantidadePersonalizar(1)
    }, [produtoPersonalizar, quantidadePersonalizar, aplicarPersonalizado, categoriaPorProduto])

    const abrirDetalhes = useCallback((produto: ProdutoCatalogo) => setProdutoDetalhe(produto), [])
    const fecharDetalhes = useCallback(() => setProdutoDetalhe(null), [])

    // Produto simples vai direto ao carrinho; com opções, segue para a escolha em passos levando a quantidade.
    const confirmarDetalhe = useCallback(({ quantidade, observacao }: EscolhaDoDetalhe) => {
        if (!produtoDetalhe) return

        setProdutoDetalhe(null)

        if (temPassos(produtoDetalhe)) {
            setQuantidadePersonalizar(quantidade)
            setProdutoPersonalizar(produtoDetalhe)
            return
        }

        adicionarComDetalhes(produtoDetalhe, { quantidade, observacao }, categoriaPorProduto.get(produtoDetalhe.id))
    }, [produtoDetalhe, adicionarComDetalhes, categoriaPorProduto])

    const renderCard = useCallback((produto: ProdutoCatalogo, cor: string | null) => (
        <CardKiosk
            key={produto.id}
            produto={produto}
            cor={cor}
            aoAdicionar={adicionar}
            aoRemover={removerSimples}
            seletorCompacto={seletorCompacto}
            aoAbrirDetalhes={abrirDetalhes}
            detalhesNaFoto={detalhesNaFoto}
        />
    ), [adicionar, removerSimples, seletorCompacto, detalhesNaFoto, abrirDetalhes])

    const fecharPersonalizacao = useCallback(() => {
        setProdutoPersonalizar(null)
        setQuantidadePersonalizar(1)
    }, [])

    return {
        carrinho,
        renderCard,
        produtoPersonalizar,
        confirmarPersonalizado,
        fecharPersonalizacao,
        produtoDetalhe,
        fecharDetalhes,
        confirmarDetalhe,
    }
}
